/**
 * Translation utilities for Admin Portal
 * Provides safe translation functions that handle type issues
 */

import { TFunction } from 'react-i18next';

/**
 * Safe translation function that ensures string return type
 */
export const safeTranslate = (t: TFunction, key: string, fallback?: string): string => {
  try {
    const translation = t(key);
    return typeof translation === 'string' ? translation : (fallback || key);
  } catch (error) {
    console.warn('Translation error for key:', key, error);
    return fallback || key;
  }
};

/**
 * Translation function that returns null if translation is not found
 * Used when you need to check if a translation exists
 */
export const getTranslation = (t: TFunction, key: string): string | null => {
  const translation = t(key);
  return typeof translation === 'string' && translation !== key ? translation : null;
};

/**
 * Hook for safe translations
 */
export const useSafeTranslation = (t: TFunction) => {
  return (key: string, fallback?: string) => safeTranslate(t, key, fallback);
};

/**
 * Hook for nullable translations
 * Returns null if translation is not found
 */
export const useNullableTranslation = (t: TFunction) => {
  return (key: string) => getTranslation(t, key);
};
